import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgePercent,
  Check,
  FileCheck2,
  GraduationCap,
  Info,
  KeyRound,
  Loader2,
  Lock,
  Receipt,
  Scale,
  Send,
  ShieldCheck,
  TrendingUp,
  UserCheck,
  X,
} from 'lucide-react';
import { byoeApi, type AcademieKorting, type ByoeUitnodiging } from '../services/byoeApi';

/**
 * Bring Your Own Employer — partnerprogramma (migratie 202).
 *
 * De professional nodigt zijn eigen opdrachtgever uit. Double-Key: de
 * opdrachtgever registreert met de link en bevestigt de samenwerking
 * (sleutel 1); na diens onboarding bevestigt de professional de match
 * (sleutel 2). Daarna: 10% opslag voor de opdrachtgever op dag 0 t/m 180, en
 * per aangebrachte opdrachtgever één jaar 10% Academie-korting (stapelbaar).
 */

const datum = (v: string | null) =>
  v ? new Date(v.length === 10 ? `${v}T12:00:00` : v).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

const VOORDELEN: Array<{ icon: React.ElementType; titel: string; tekst: string }> = [
  {
    icon: ShieldCheck,
    titel: 'Wet DBA: vrije vervanging aantoonbaar',
    tekst:
      'Ben je met vakantie of ziek, dan sluit je je agenda en kunnen medewerkers via de bedrijfspool bij andere specialisten terecht. Zo laat je zien dat de opdracht niet aan jou persoonlijk gebonden is — een van de factoren die de Belastingdienst weegt.',
  },
  {
    icon: Lock,
    titel: 'Centrale, beveiligde dossiers',
    tekst:
      'Gespreksverslagen, actieplannen en dossiers staan in de beveiligde BeLagom-omgeving in plaats van op je eigen computer of in Excel-lijsten. Gevoelige inhoud wordt versleuteld opgeslagen en de toegang is per rol afgeschermd.',
  },
  {
    icon: UserCheck,
    titel: 'Rechtstreekse toegang tot de werkvloer',
    tekst:
      'Medewerkers vinden je zelf en vertrouwelijk via hun eigen portaal, zonder dat HR of een manager hen hoeft door te verwijzen. Zo werk je aan de preventieve voorkant van verzuim.',
  },
  {
    icon: KeyRound,
    titel: 'Privacy by design',
    tekst:
      'De medewerker meldt zich anoniem aan, documenten (sjabloon A/B/C) staan in de beveiligde omgeving en de communicatie loopt via de in-app chat. Er gaan geen privacygevoelige e-mails of namen meer naar de HR-afdeling.',
  },
  {
    icon: FileCheck2,
    titel: 'Geen urendiscussies meer',
    tekst:
      'Elke boeking, consultatie en terugbelactie wordt digitaal vastgelegd. Jij en je opdrachtgever kijken naar dezelfde registratie, dus de factuur sluit aan op wat beide partijen zien.',
  },
  {
    icon: Receipt,
    titel: 'Facturatie gaat vanzelf',
    tekst:
      'Op de 1e van de maand maakt het platform de verzamelfactuur op basis van de voltooide afspraken en loopt je uitbetaling via Stripe Connect, met self-billing en btw per factuurregel.',
  },
  {
    icon: TrendingUp,
    titel: 'Kwaliteit zichtbaar bij de directie',
    tekst:
      'Reviews, je Kwaliteitspaspoort en geanonimiseerde rapportages laten de opdrachtgever zien wat jouw inzet oplevert — een sterke basis voor verlenging.',
  },
];

const STAP_LABELS = ['Uitgenodigd', 'Opdrachtgever geregistreerd', 'Onboarding afgerond', 'Jij bevestigt de match'];

/** Aantal afgeronde Double-Key-stappen (0–4). */
function stappen(u: ByoeUitnodiging): number {
  if (u.status === 'VERIFIED') return 4;
  if (u.status === 'REGISTERED') return u.opdrachtgever?.onboarding_afgerond ? 3 : 2;
  if (u.status === 'PENDING_REGISTRATION') return 1;
  return 0;
}

const STATUS: Record<string, { tekst: string; klasse: string }> = {
  PENDING_REGISTRATION: { tekst: 'Uitgenodigd', klasse: 'bg-amber-50 text-amber-800 border-amber-200' },
  EXPIRED: { tekst: 'Link verlopen', klasse: 'bg-gray-100 text-gray-600 border-gray-200' },
  REVOKED: { tekst: 'Ingetrokken', klasse: 'bg-gray-100 text-gray-500 border-gray-200' },
  REJECTED: { tekst: 'Afgewezen', klasse: 'bg-gray-100 text-gray-500 border-gray-200' },
  REGISTERED: { tekst: 'Wacht op bevestiging', klasse: 'bg-blue-50 text-blue-800 border-blue-200' },
  VERIFIED: { tekst: 'Geverifieerd', klasse: 'bg-green-50 text-green-800 border-green-200' },
};

const AcademieKaart: React.FC<{ a: AcademieKorting }> = ({ a }) => (
  <div className="bg-[#0B1F3A] text-white rounded-xl p-5">
    <div className="flex items-center gap-2 text-sm text-slate-300">
      <GraduationCap className="w-4 h-4" /> Jouw Academie-korting
    </div>
    <div className="mt-2 flex items-baseline gap-2">
      <span className="text-4xl font-extrabold">{a.korting_pct}%</span>
      <span className="text-slate-300">{a.premium_actief ? 'premium' : 'standaard'}</span>
    </div>
    {a.premium_actief ? (
      <p className="mt-2 text-sm text-slate-200">
        Nog {a.premium_jaren} {a.premium_jaren === 1 ? 'jaar' : 'jaar'} {a.premium_pct}% korting, t/m{' '}
        <strong>{datum(a.premium_tot_en_met)}</strong>.
      </p>
    ) : (
      <p className="mt-2 text-sm text-slate-200">
        Breng een opdrachtgever aan en je korting gaat een vol jaar van {a.standaard_pct}% naar {a.premium_pct}%.
      </p>
    )}
    <p className="mt-3 text-xs text-slate-400">
      {a.aangebrachte_opdrachtgevers} aangebrachte opdrachtgever{a.aangebrachte_opdrachtgevers === 1 ? '' : 's'}
    </p>
  </div>
);

export const ProfessionalByoePage: React.FC = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ bedrijfsnaam: '', contact_naam: '', email: '' });
  const { data, isLoading } = useQuery({ queryKey: ['byoe'], queryFn: byoeApi.overzicht });

  const fout = (err: any) => toast.error(err?.response?.data?.message || 'Er is iets misgegaan');
  const ververs = () => queryClient.invalidateQueries({ queryKey: ['byoe'] });

  const nodigUit = useMutation({
    mutationFn: byoeApi.nodigUit,
    onSuccess: () => {
      toast.success('Uitnodiging verstuurd');
      setForm({ bedrijfsnaam: '', contact_naam: '', email: '' });
      ververs();
    },
    onError: fout,
  });
  const trekIn = useMutation({ mutationFn: byoeApi.trekIn, onSuccess: ververs, onError: fout });
  const wijsAf = useMutation({ mutationFn: byoeApi.wijsAf, onSuccess: ververs, onError: fout });
  const bevestig = useMutation({
    mutationFn: byoeApi.bevestig,
    onSuccess: () => {
      toast.success('Match bevestigd: je Academie-korting is met een jaar verlengd');
      ververs();
      queryClient.invalidateQueries({ queryKey: ['professional-academy'] });
    },
    onError: fout,
  });

  const opnieuw = (u: ByoeUitnodiging) =>
    nodigUit.mutate({ email: u.email, bedrijfsnaam: u.bedrijfsnaam, contact_naam: u.contact_naam ?? undefined });

  const academie = data?.academie;
  const jaren = academie?.premium_jaren ?? 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bring Your Own Employer</h1>
        <p className="text-gray-600 mt-1 max-w-3xl">
          Neem je vaste opdrachtgever mee naar BeLagom. Jullie samenwerking loopt dan via één beveiligde omgeving, je
          klant betaalt de eerste 6 maanden minder opslag en jij krijgt per aangebrachte klant een vol jaar dubbele
          Academie-korting.
        </p>
      </div>

      {/* 1️⃣ De wederzijdse incentive */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 font-semibold text-gray-900">
            <BadgePercent className="w-5 h-5 text-emerald-600" /> Voordeel voor je klant
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-gray-900">10%</span>
            <span className="text-gray-400 line-through">15%</span>
            <span className="text-sm text-gray-600">platform-opslag</span>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            De eerste 6 maanden (dag 0 t/m 180 na het aanmaken van het werkgeversaccount) op jouw uurtarief. Daarna geldt
            automatisch de standaard 15%. Jij ontvangt altijd 100% van je tarief.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 lg:col-span-1">
          <div className="flex items-center gap-2 font-semibold text-gray-900">
            <GraduationCap className="w-5 h-5 text-emerald-600" /> Voordeel voor jou: stapelbaar
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Standaard krijg je 5% korting op geaccrediteerde opleidingen en PE-cursussen. Per aangebrachte opdrachtgever
            wordt dat <strong>een vol jaar 10%</strong> — onbeperkt stapelbaar.
          </p>
          <div className="mt-4 space-y-2" aria-label="Jaar-staffel">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex items-center gap-3 text-sm">
                <span className="w-20 shrink-0 text-gray-600">
                  {n} klant{n > 1 ? 'en' : ''}
                </span>
                <div className="flex gap-1 flex-1">
                  {Array.from({ length: 3 }, (_, i) => (
                    <span
                      key={i}
                      className={`h-3 flex-1 rounded ${i < n ? (i < jaren ? 'bg-emerald-600' : 'bg-emerald-300') : 'bg-gray-100'}`}
                    />
                  ))}
                </div>
                <span className="w-16 shrink-0 text-right font-semibold text-gray-900">
                  {n} jaar
                </span>
              </div>
            ))}
          </div>
        </div>

        {academie ? (
          <AcademieKaart a={academie} />
        ) : (
          <div className="bg-[#0B1F3A] rounded-xl p-5 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}
      </div>

      {/* Uitnodigen */}
      <form
        className="bg-white border border-gray-200 rounded-xl p-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          nodigUit.mutate({
            bedrijfsnaam: form.bedrijfsnaam.trim(),
            contact_naam: form.contact_naam.trim() || undefined,
            email: form.email.trim(),
          });
        }}
      >
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Send className="w-5 h-5" /> Opdrachtgever uitnodigen
        </h2>
        {data && !data.mag_uitnodigen && (
          <p className="text-sm rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2">
            Het partnerprogramma is beschikbaar zodra je screening is goedgekeurd.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            required
            minLength={2}
            className="px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Bedrijfsnaam"
            value={form.bedrijfsnaam}
            onChange={(e) => setForm({ ...form, bedrijfsnaam: e.target.value })}
          />
          <input
            className="px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Naam contactpersoon (HR)"
            value={form.contact_naam}
            onChange={(e) => setForm({ ...form, contact_naam: e.target.value })}
          />
          <input
            type="email"
            required
            className="px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Zakelijk e-mailadres"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500 max-w-xl">
            Alleen voor opdrachtgevers die nog geen BeLagom-account hebben. De link is 7 dagen geldig. De voordelen gaan
            in zodra beide sleutels zijn omgedraaid: je klant registreert en bevestigt de samenwerking, en jij bevestigt
            daarna de match.
          </p>
          <button
            type="submit"
            disabled={nodigUit.isPending || (data ? !data.mag_uitnodigen : true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B1F3A] text-white font-medium hover:bg-[#1A3459] disabled:opacity-60"
          >
            {nodigUit.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Uitnodigen
          </button>
        </div>
      </form>

      {/* Uitnodigingen + Double-Key-stand */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-4 border-b border-gray-100 font-semibold text-gray-900">Mijn aangebrachte opdrachtgevers</div>
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : !data || data.uitnodigingen.length === 0 ? (
          <p className="p-5 text-sm text-gray-500">Nog geen uitnodigingen verstuurd.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.uitnodigingen.map((u) => {
              const s = STATUS[u.status] ?? STATUS.EXPIRED;
              const klaar = stappen(u);
              const actief = u.status !== 'EXPIRED' && u.status !== 'REVOKED' && u.status !== 'REJECTED';
              return (
                <li key={u.id} className="px-5 py-4 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-gray-900">{u.opdrachtgever?.bedrijfsnaam ?? u.bedrijfsnaam}</div>
                      <div className="text-sm text-gray-500">
                        {u.email}
                        {u.opdrachtgever?.kvk_nummer && <> · KvK {u.opdrachtgever.kvk_nummer}</>}
                      </div>
                    </div>
                    <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${s.klasse}`}>{s.tekst}</span>
                  </div>

                  {actief && (
                    <ol className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      {STAP_LABELS.map((label, i) => (
                        <li
                          key={label}
                          className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 ${
                            i < klaar ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-500'
                          }`}
                        >
                          {i < klaar ? <Check className="w-3.5 h-3.5 shrink-0" /> : <span className="w-3.5 text-center">{i + 1}</span>}
                          {label}
                        </li>
                      ))}
                    </ol>
                  )}

                  {u.status === 'VERIFIED' && (
                    <p className="text-sm text-gray-700">
                      Opslag voor deze klant: <strong>{u.huidige_fee_percentage}%</strong>
                      {u.huidige_fee_percentage === 10 ? <> t/m {datum(u.fee_verlaagd_tot)}, daarna 15%.</> : '.'} Bevestigd op{' '}
                      {datum(u.verified_at)}.
                    </p>
                  )}
                  {u.status === 'REGISTERED' && !u.kan_bevestigen && (
                    <p className="flex items-start gap-2 text-sm text-gray-600">
                      <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
                      {u.opdrachtgever?.onboarding_afgerond
                        ? 'De opdrachtgever heeft nog geen KvK-nummer vastgelegd.'
                        : 'De opdrachtgever rondt de onboarding af (KvK-gegevens en overeenkomst). Daarna kun je de match bevestigen.'}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-3 text-sm">
                    {u.kan_bevestigen && (
                      <>
                        <button
                          onClick={() => bevestig.mutate(u.id)}
                          disabled={bevestig.isPending}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {bevestig.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                          Dit is mijn opdrachtgever — match bevestigen
                        </button>
                        <button
                          onClick={() => wijsAf.mutate(u.id)}
                          className="text-gray-500 hover:text-red-600"
                          title="Je herkent deze organisatie niet"
                        >
                          Niet herkend
                        </button>
                      </>
                    )}
                    {u.status === 'PENDING_REGISTRATION' && (
                      <button onClick={() => trekIn.mutate(u.id)} className="inline-flex items-center gap-1 text-gray-500 hover:text-red-600">
                        <X className="w-4 h-4" /> Intrekken
                      </button>
                    )}
                    {(u.status === 'EXPIRED' || u.status === 'REVOKED') && (
                      <button onClick={() => opnieuw(u)} className="text-blue-600 hover:text-blue-700">
                        Opnieuw uitnodigen
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Overige voordelen */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Waarom je klant via BeLagom laten lopen</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {VOORDELEN.map(({ icon: Icon, titel, tekst }) => (
            <div key={titel} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2 font-semibold text-gray-900">
                <Icon className="w-5 h-5 text-[#0B1F3A]" /> {titel}
              </div>
              <p className="mt-2 text-sm text-gray-600">{tekst}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 flex items-start gap-2 text-xs text-gray-500">
          <Scale className="w-4 h-4 shrink-0" />
          BeLagom ondersteunt een werkwijze die past bij zelfstandig ondernemerschap, maar geeft geen garantie op de
          fiscale kwalificatie: de Belastingdienst beoordeelt de feitelijke werkrelatie als geheel.
        </p>
      </div>
    </div>
  );
};

export default ProfessionalByoePage;
